import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Icons from 'lucide-react';

const COOKIE_CONSENT_KEY = 'cookie_consent';

const CookieConsent: React.FC = () => {
  const { t } = useTranslation('common');
  const [visible, setVisible] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay so it doesn't flash on page load
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // The banner is `fixed`, so it floats over the page. On short viewports that put it
  // on top of form controls (the register page's consent checkboxes and submit button),
  // which made them unclickable. Reserve its height at the bottom of the document while
  // it is shown, so everything stays reachable by scrolling.
  useEffect(() => {
    if (!visible) {
      document.body.style.paddingBottom = '';
      document.documentElement.style.removeProperty('--cookie-consent-space');
      return;
    }

    const reserve = () => {
      const height = bannerRef.current?.offsetHeight ?? 0;
      document.body.style.paddingBottom = height ? `${height + 16}px` : '';
      document.documentElement.style.setProperty('--cookie-consent-space', `${height + 16}px`);
    };
    reserve();

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(reserve) : null;
    if (observer && bannerRef.current) observer.observe(bannerRef.current);
    window.addEventListener('resize', reserve);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', reserve);
      document.body.style.paddingBottom = '';
      document.documentElement.style.removeProperty('--cookie-consent-space');
    };
  }, [visible]);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, new Date().toISOString());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      className="fixed bottom-0 left-0 right-0 z-50 px-3 py-1 sm:px-4 sm:py-2 animate-in slide-in-from-bottom-4 duration-500"
    >
      <div className="max-w-3xl mx-auto bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-2xl shadow-elevated px-3 py-2 sm:px-4 sm:py-2 flex flex-row max-[360px]:flex-col max-[360px]:items-stretch items-center gap-3 sm:gap-4">
        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
          <div className="w-8 h-8 sm:w-9 sm:h-9 max-[360px]:hidden bg-accent-soft-light dark:bg-accent-soft-dark rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
            <Icons.Cookie className="w-5 h-5 text-brand-600 dark:text-brand-400" />
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug">
            {t('cookieConsent.message')}{' '}
            <Link
              to="/cookies"
              className="text-brand-600 dark:text-brand-400 font-semibold hover:underline"
            >
              {t('cookieConsent.cookiePolicy')}
            </Link>{' '}
            {t('cookieConsent.and')}{' '}
            <Link
              to="/privacy"
              className="text-brand-600 dark:text-brand-400 font-semibold hover:underline"
            >
              {t('cookieConsent.privacyPolicy')}
            </Link>
            .
          </p>
        </div>
        <button
          onClick={handleAccept}
          className="min-h-11 max-[360px]:w-full bg-brand-600 text-white px-3 sm:px-6 rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-widest hover:bg-brand-700 active:scale-95 transition-all shadow-lg shadow-brand-200 dark:shadow-none whitespace-nowrap"
        >
          {t('cookieConsent.accept')}
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;
